import { collectSSE, safeJson } from './sse';
import { providerFetch } from './transport';
import { LLMError, type ChatMeta, type ChatRequest, type ContentPart, type LLMAdapter } from './types';

type OpenAIPart = { type: 'text'; text: string } | { type: 'image_url'; image_url: { url: string } };

function toPart(p: ContentPart): OpenAIPart {
  return p.type === 'text' ? { type: 'text', text: p.text } : { type: 'image_url', image_url: { url: `data:${p.mimeType};base64,${p.base64}` } };
}

interface StreamChunk { choices?: { delta?: { content?: string | null } }[]; usage?: { prompt_tokens?: number; completion_tokens?: number } | null }
interface Completion { choices?: { message?: { content?: string | null } }[]; usage?: { prompt_tokens?: number; completion_tokens?: number } }

const NON_CHAT = /realtime|audio|tts|transcribe|whisper|embedding|moderation|dall-e|image|search|instruct|codex|computer-use|deep-research/i;

function body(req: ChatRequest, stream: boolean): string {
  return JSON.stringify({
    model: req.model,
    stream,
    ...(stream ? { stream_options: { include_usage: true } } : {}),
    messages: [{ role: 'system', content: req.system }, ...req.messages.map((m) => ({ role: m.role, content: m.content.map(toPart) }))],
    ...(req.jsonMode ? { response_format: { type: 'json_object' } } : {}),
  });
}

function post(req: ChatRequest, stream: boolean) {
  return providerFetch('openai', '/v1/chat/completions', { method: 'POST', headers: { 'content-type': 'application/json' }, body: body(req, stream), signal: req.signal }, req.apiKey);
}

/** Responses API output (only the fields we read). Web search lives here, not in Chat Completions. */
export interface ResponsesResult {
  output?: (
    | { type: 'web_search_call'; action?: { type?: string; query?: string; queries?: string[]; sources?: { url?: string; title?: string }[] } }
    | { type: 'message'; content?: { type?: string; text?: string; annotations?: { type?: string; url?: string; title?: string }[] }[] }
    | { type: string }
  )[];
  usage?: { input_tokens?: number; output_tokens?: number };
}

/** Pulls text, searches, and cited URLs out of a Responses API result. Pure; exported for tests. */
export function extractResponses(json: ResponsesResult): { text: string; queries: string[]; sources: { url: string; title?: string }[]; searches: number } {
  let text = '';
  const queries: string[] = [];
  const sources: { url: string; title?: string }[] = [];
  let searches = 0;
  for (const item of json.output ?? []) {
    if (item.type === 'web_search_call' && 'action' in item) {
      const a = item.action;
      if (!a || a.type === undefined || a.type === 'search') {
        searches += 1;
        if (a?.query) queries.push(a.query);
        for (const q of a?.queries ?? []) queries.push(q);
        for (const s of a?.sources ?? []) if (s.url) sources.push({ url: s.url, title: s.title });
      }
    } else if (item.type === 'message' && 'content' in item) {
      for (const c of item.content ?? []) {
        if (c.type === 'output_text' && c.text) text += c.text;
        for (const an of c.annotations ?? []) if (an.type === 'url_citation' && an.url) sources.push({ url: an.url, title: an.title });
      }
    }
  }
  const seen = new Set<string>();
  return { text, queries, searches, sources: sources.filter((s) => (seen.has(s.url) ? false : (seen.add(s.url), true))) };
}

/** Web-enabled call through /v1/responses with the built-in web_search tool (Chat Completions has no general search tool). */
async function chatWithSearch(req: ChatRequest): Promise<string> {
  const input = req.messages.map((m) => ({
    role: m.role,
    content: m.content.map((p) =>
      p.type === 'text'
        ? { type: m.role === 'assistant' ? 'output_text' : 'input_text', text: p.text }
        : { type: 'input_image', image_url: `data:${p.mimeType};base64,${p.base64}` },
    ),
  }));
  const payload = {
    model: req.model,
    instructions: req.system,
    input,
    tools: [{ type: 'web_search' }],
    tool_choice: 'auto',
    include: ['web_search_call.action.sources'],
  };
  const res = await providerFetch('openai', '/v1/responses', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload), signal: req.signal }, req.apiKey);
  const json = (await res.json()) as ResponsesResult;
  const out = extractResponses(json);
  const meta: ChatMeta = { inputTokens: json.usage?.input_tokens, outputTokens: json.usage?.output_tokens, searches: out.searches, queries: out.queries, sources: out.sources };
  req.onMeta?.(meta);
  if (out.text) req.onDelta?.(out.text);
  return out.text;
}

export const openaiAdapter: LLMAdapter = {
  provider: 'openai',
  async chat(req) {
    if (req.webSearch) return chatWithSearch(req);
    let res: Response;
    try {
      res = await post(req, true);
    } catch (e) {
      // Some models refuse to stream for unverified organizations; the same request works unstreamed.
      if (!(e instanceof LLMError && e.status === 400 && /stream/i.test(e.message))) throw e;
      const full = (await (await post(req, false)).json()) as Completion;
      const text = full.choices?.[0]?.message?.content ?? '';
      req.onMeta?.({ inputTokens: full.usage?.prompt_tokens, outputTokens: full.usage?.completion_tokens });
      if (text) req.onDelta?.(text);
      return text;
    }
    const usage: ChatMeta = {};
    const text = await collectSSE(res, (data) => {
      const chunk = safeJson(data) as StreamChunk | undefined;
      if (chunk?.usage) {
        usage.inputTokens = chunk.usage.prompt_tokens;
        usage.outputTokens = chunk.usage.completion_tokens;
      }
      const delta = chunk?.choices?.[0]?.delta?.content;
      return typeof delta === 'string' ? delta : undefined;
    }, req.onDelta);
    req.onMeta?.(usage);
    return text;
  },
  async listModels(apiKey, signal) {
    const json = (await (await providerFetch('openai', '/v1/models', { signal }, apiKey)).json()) as { data?: { id: string; created?: number }[] };
    return (json.data ?? [])
      .filter((m) => /^(gpt-|o\d|chatgpt-)/.test(m.id) && !NON_CHAT.test(m.id))
      .sort((a, b) => (b.created ?? 0) - (a.created ?? 0) || a.id.localeCompare(b.id))
      .map((m) => m.id);
  },
};
